// Wishlist — localStorage store of product handles, card toggles, header counter,
// and a left-side drawer (sticky FAB) that lists saved products.
class Wishlist {
  constructor() {
    this.wishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
    this.counter = document.getElementById("wishlist-counter");
    this.cache = {}; // handle -> product JSON
    this.initButtons();
    this.setInitialStates();
    this.updateCounter();
    this.initDrawer();
  }

  setInitialStates() {
    document.querySelectorAll("[data-wishlist-toggle]").forEach((button) => {
      const productHandle = button.dataset.productHandle;
      const active = this.isInWishlist(productHandle);
      button.classList.toggle("in-wishlist", active);
      button.classList.toggle("remove", active);
      button.setAttribute(
        "aria-label",
        active ? "Remove from wishlist" : "Add to wishlist"
      );
    });
  }

  updateCounter() {
    if (!this.counter) this.counter = document.getElementById("wishlist-counter");
    const count = this.wishlist.length || 0;
    if (this.counter) {
      this.counter.textContent = count;
      this.counter.classList.toggle("empty", count === 0);
    }
    // Sticky FAB badge + drawer header count
    const fabCount = document.querySelector("[data-wl-count]");
    if (fabCount) {
      fabCount.textContent = count;
      fabCount.hidden = count === 0;
    }
    const headCount = document.querySelector("[data-wl-count-text]");
    if (headCount) headCount.textContent = count;
  }

  initButtons() {
    document.addEventListener("click", (e) => {
      const button = e.target.closest("[data-wishlist-toggle]");
      if (button) {
        e.preventDefault();
        this.toggleProduct(button.dataset.productHandle, button);
      }
    });
  }

  toggleProduct(handle, button) {
    const index = this.wishlist.indexOf(handle);
    if (index > -1) {
      this.wishlist.splice(index, 1);
      if (button) {
        button.classList.remove("in-wishlist", "remove");
        button.setAttribute("aria-label", "Add to wishlist");
      }
    } else {
      this.wishlist.push(handle);
      if (button) {
        button.classList.add("in-wishlist", "remove");
        button.setAttribute("aria-label", "Remove from wishlist");
      }
    }
    this.updateCounter();
    localStorage.setItem("wishlist", JSON.stringify(this.wishlist));
    // Keep any other card buttons for this handle in sync.
    this.setInitialStates();

    document.dispatchEvent(
      new CustomEvent("wishlist:updated", {
        detail: { handle: handle, action: index > -1 ? "removed" : "added" },
      })
    );
  }

  isInWishlist(handle) {
    return this.wishlist.includes(handle);
  }

  // ===== Drawer =====
  initDrawer() {
    this.drawer = document.querySelector("[data-wl-drawer]");
    this.overlay = document.querySelector("[data-wl-overlay]");
    this.body = document.querySelector("[data-wl-body]");
    this.emptyTpl = document.querySelector("[data-wl-empty-template]");
    if (!this.drawer) return;

    document.querySelectorAll("[data-wl-open]").forEach((b) =>
      b.addEventListener("click", () => this.openDrawer())
    );
    document.querySelectorAll("[data-wl-close]").forEach((b) =>
      b.addEventListener("click", () => this.closeDrawer())
    );
    if (this.overlay) this.overlay.addEventListener("click", () => this.closeDrawer());
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.drawer.classList.contains("is-open")) this.closeDrawer();
    });

    // Delegate item actions.
    this.body.addEventListener("click", (e) => {
      const remove = e.target.closest("[data-wl-remove]");
      if (remove) {
        this.toggleProduct(remove.closest("[data-wl-item]").dataset.handle, null);
        this.render();
        return;
      }
      const add = e.target.closest("[data-wl-add]");
      if (add && !add.disabled) this.addToCart(add);
    });

    // Re-render whenever the wishlist changes elsewhere.
    document.addEventListener("wishlist:updated", () => {
      if (this.drawer.classList.contains("is-open")) this.render();
    });
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    // Force a reflow so the closed transform (translateX(-100%)) is committed
    // before we add .is-open — otherwise the browser batches the display change
    // with the class change and skips the open transition.
    void this.drawer.offsetWidth;
    this.drawer.classList.add("is-open");
    if (this.overlay) this.overlay.classList.add("is-open");
    document.documentElement.style.overflow = "hidden";
    this.render();
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove("is-open");
    if (this.overlay) this.overlay.classList.remove("is-open");
    document.documentElement.style.overflow = "";
    setTimeout(() => {
      this.drawer.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
    }, 320);
  }

  formatMoney(cents) {
    const value = (cents || 0) / 100;
    const fmt =
      window.money_with_currency_format || window.money_format || "{{amount}}";
    const amount = value
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const noDec = Math.round(value)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount);
  }

  async fetchProduct(handle) {
    if (this.cache[handle]) return this.cache[handle];
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    const base = root === "/" ? "" : root.replace(/\/$/, "");
    const res = await fetch(base + "/products/" + handle + ".js");
    if (!res.ok) throw new Error("not found");
    const data = await res.json();
    this.cache[handle] = data;
    return data;
  }

  async render() {
    if (!this.body) return;
    this.updateCounter();

    if (this.wishlist.length === 0) {
      this.body.innerHTML = "";
      if (this.emptyTpl) this.body.appendChild(this.emptyTpl.content.cloneNode(true));
      return;
    }

    this.body.innerHTML = '<div class="wl-items" data-wl-items></div>';
    const list = this.body.querySelector("[data-wl-items]");

    const handles = this.wishlist.slice();
    const products = await Promise.all(
      handles.map((h) => this.fetchProduct(h).catch(() => null))
    );

    products.forEach((p, i) => {
      if (!p) return; // product removed / unpublished — skip gracefully
      list.insertAdjacentHTML("beforeend", this.itemHTML(p, handles[i]));
    });
  }

  itemHTML(p, handle) {
    let variant =
      (p.variants || []).find((v) => v.available) || (p.variants || [])[0] || {};
    const soldOut = !variant.available;
    let variantLabel = "";
    if (variant.title && variant.title !== "Default Title") {
      variantLabel = (variant.options || [variant.title]).join(" / ");
    }
    const img = p.featured_image || (p.images && p.images[0]) || "";
    const url = ((window.Shopify && Shopify.routes && Shopify.routes.root) || "/").replace(/\/$/, "") + "/products/" + handle;
    const price = this.formatMoney(variant.price != null ? variant.price : p.price);

    return (
      '<div class="wl-item" data-wl-item data-handle="' + handle + '">' +
        '<div class="wl-item__top">' +
          (img ? '<img class="wl-item__img" src="' + img + '" alt="" loading="lazy">' : '<span class="wl-item__img"></span>') +
          '<div class="wl-item__info">' +
            '<a class="wl-item__title" href="' + url + '">' + this.esc(p.title) + '</a>' +
            (variantLabel ? '<p class="wl-item__variant">' + this.esc(variantLabel) + '</p>' : '') +
            '<p class="wl-item__price">' + price + '</p>' +
          '</div>' +
        '</div>' +
        '<div class="wl-item__bottom">' +
          '<button class="wl-item__remove" type="button" data-wl-remove aria-label="Remove">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>' +
          '</button>' +
          (soldOut
            ? '<button class="wl-item__add is-soldout" type="button" disabled>Out of stock</button>'
            : '<button class="wl-item__add" type="button" data-wl-add data-variant-id="' + variant.id + '">Add to cart</button>') +
        '</div>' +
      '</div>'
    );
  }

  esc(str) {
    return String(str == null ? "" : str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  async addToCart(btn) {
    const id = btn.getAttribute("data-variant-id");
    if (!id) return;
    btn.classList.add("is-loading");
    const label = btn.textContent;
    btn.textContent = "Adding…";
    try {
      const url =
        (window.routes && window.routes.cart_add_url
          ? window.routes.cart_add_url
          : "/cart/add") + ".js";
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error("add failed");
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "wishlist" });
      }
      this.closeDrawer();
      const openCart = document.querySelector("[data-open-cart]");
      if (openCart) openCart.click();
    } catch (e) {
      btn.textContent = label;
    } finally {
      btn.classList.remove("is-loading");
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  window.wishlist = new Wishlist();
});

document.addEventListener("shopify:section:load", () => {
  if (window.wishlist) window.wishlist.setInitialStates();
});
